/**
 * @file 作品の問題一覧 (共通データ) を取得する hook。
 *
 * @what: work_id ごとに1回読む。空文字なら何もしない (idle)。
 * @why: 解説の面とリーダーの両方が「いまのページの問題」を引くために同じ一覧を要る。
 * @failure: 失敗は `error` (画面は1行で知らせる。詳細は console のみ)。404も error に含める。
 */

import { useEffect, useState } from "react";
import { fetchStudyWork, type StudyWorkIndex } from "../api/studyWorks";

export type StudyWorkState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; index: StudyWorkIndex }
  | { status: "error" };

export function useStudyWork(workId: string): StudyWorkState {
  const [state, setState] = useState<StudyWorkState>({ status: "idle" });

  useEffect(() => {
    if (!workId) {
      setState({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchStudyWork(workId, controller.signal)
      .then((index) => {
        if (!controller.signal.aborted) setState({ status: "ready", index });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load study work", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [workId]);

  return state;
}
